import type { LocalizedDict } from '../index'

export type PartnerApiStrings = {
  keys: {
    keyCopied: string
    copyFailed: string
    revealTitle: string
    revealBody: string
    copied: string
    copy: string
    sendHint: string
    doneCopying: string
    issueFailed: string
    revoked: string
    revokeFailed: string
    title: string
    description: string
    guideLink: string
    guideHint: string
    issueHeading: string
    partner: string
    chooseOrg: string
    label: string
    labelPlaceholder: string
    issuing: string
    issueKey: string
    labelHint: string
    columns: {
      partner: string
      label: string
      key: string
      productsShared: string
      lastUsed: string
      status: string
    }
    empty: string
    statusActive: string
    statusRevoked: string
    statusExpired: string
    revokeForGood: string
    cancel: string
    revokeTitle: string
    revoke: string
    footnote: string
  }
  switch: {
    blockerSwitchOff: string
    blockerNoKey: string
    blockerNoProducts: string
    title: string
    description: string
    toggleLabel: string
    on: string
    off: string
    blocked: (reasons: string[]) => string
    live: string
    readFailed: string
    writeFailed: string
  }
  catalog: {
    opened: (n: number) => string
    closed: (n: number) => string
    unchanged: (n: number) => string
    skipped: (n: number) => string
    nothingToChange: string
    applyFailed: string
    title: string
    descriptionBefore: string
    descriptionToday: string
    descriptionAfter: string
    partner: string
    choosePartner: string
    columns: {
      category: string
      shared: string
      notShared: string
    }
    loading: string
    noProducts: string
    grownTitle: string
    hideProducts: string
    reviewProducts: string
    loadingProducts: string
    openAllUnshared: (n: number) => string
    openSelected: (n: number) => string
    closeAllShared: (n: number) => string
    sharedBadge: string
    closedBadge: string
  }
}

export const partnerApiI18n: LocalizedDict<PartnerApiStrings> = {
  en: {
    keys: {
      keyCopied: 'Key copied',
      copyFailed: 'Could not copy — select the key and copy it manually',
      revealTitle: 'Copy this key now — it is shown once.',
      revealBody:
        'Only a hash is stored, so it cannot be shown again. If it is lost, issue a new key and revoke this one.',
      copied: 'Copied',
      copy: 'Copy',
      sendHint: 'Send it to the partner over a channel you trust — not email or chat.',
      doneCopying: 'I have copied it',
      issueFailed: 'Could not issue the key',
      revoked: 'Key revoked — it stops working on the next request',
      revokeFailed: 'Could not revoke the key',
      title: 'Partner API',
      description:
        'Keys that let an outside company read the catalogue you have opened to it. Read only: a key cannot place orders, change prices or see anything you have not assigned to that organisation.',
      guideLink: 'Integration guide for partners →',
      guideHint: 'send this alongside the key',
      issueHeading: 'Issue a key',
      partner: 'Partner',
      chooseOrg: 'Choose an organisation…',
      label: 'Label',
      labelPlaceholder: 'Nightly sync',
      issuing: 'Issuing…',
      issueKey: 'Issue key',
      labelHint: 'The label is for you — it is how you tell two keys for the same partner apart.',
      columns: {
        partner: 'Partner',
        label: 'Label',
        key: 'Key',
        productsShared: 'Products shared',
        lastUsed: 'Last used',
        status: 'Status',
      },
      empty: 'No keys issued yet.',
      statusActive: 'Active',
      statusRevoked: 'Revoked',
      statusExpired: 'Expired',
      revokeForGood: 'Revoke for good',
      cancel: 'Cancel',
      revokeTitle: 'The partner stops being able to read on its next request',
      revoke: 'Revoke',
      footnote:
        'A revoked key stays listed on purpose — it is the record that a partner had access and no longer does.',
    },
    switch: {
      blockerSwitchOff: 'the switch below is off',
      blockerNoKey: 'no key has been issued',
      blockerNoProducts: 'no products have been shared',
      title: 'Partner API',
      description:
        'The master switch for every partner key. While it is off the endpoints answer as if they do not exist, so a key that leaks before you are ready opens nothing.',
      toggleLabel: 'Partners may read the catalogue',
      on: 'On',
      off: 'Off',
      blocked: (reasons) => `Nobody can read anything yet — ${reasons.join(', and ')}.`,
      live: 'Live. Partners with an active key can read the products shared with them.',
      readFailed:
        'The switch could not be read, so it is shown as off — which is how the server behaves too.',
      writeFailed: 'Could not change the switch. It is still whatever the server last stored.',
    },
    catalog: {
      opened: (n) => `${n} opened`,
      closed: (n) => `${n} closed`,
      unchanged: (n) => `${n} unchanged`,
      skipped: (n) => `${n} skipped (not offerable)`,
      nothingToChange: 'Nothing to change',
      applyFailed: 'Could not apply the change',
      title: 'Partner catalogue access',
      descriptionBefore:
        'What each partner is allowed to read. Nothing is shared until you share it, and opening a category shares what is in it ',
      descriptionToday: 'today',
      descriptionAfter: ' — a product added later stays closed until you open it too.',
      partner: 'Partner',
      choosePartner: 'Choose a partner…',
      columns: {
        category: 'Category',
        shared: 'Shared',
        notShared: 'Not shared',
      },
      loading: 'Loading…',
      noProducts: 'No products in the catalogue.',
      grownTitle: 'This category has grown since you opened it',
      hideProducts: 'Hide products',
      reviewProducts: 'Review products',
      loadingProducts: 'Loading products…',
      openAllUnshared: (n) => `Open all ${n} not shared`,
      openSelected: (n) => `Open selected (${n})`,
      closeAllShared: (n) => `Close all ${n} shared`,
      sharedBadge: 'shared',
      closedBadge: 'closed',
    },
  },
  tr: {
    keys: {
      keyCopied: 'Anahtar kopyalandı',
      copyFailed: 'Kopyalanamadı — anahtarı seçip elle kopyalayın',
      revealTitle: 'Bu anahtarı şimdi kopyalayın — yalnızca bir kez gösterilir.',
      revealBody:
        'Yalnızca özeti saklanır, bu yüzden bir daha gösterilemez. Kaybolursa yeni bir anahtar oluşturun ve bunu iptal edin.',
      copied: 'Kopyalandı',
      copy: 'Kopyala',
      sendHint: 'İş ortağına güvendiğiniz bir kanaldan gönderin — e-posta ya da sohbetten değil.',
      doneCopying: 'Kopyaladım',
      issueFailed: 'Anahtar oluşturulamadı',
      revoked: 'Anahtar iptal edildi — bir sonraki istekte çalışmaz',
      revokeFailed: 'Anahtar iptal edilemedi',
      title: 'İş Ortağı API',
      description:
        'Dışarıdaki bir firmanın, ona açtığınız kataloğu okumasını sağlayan anahtarlar. Yalnızca okuma: anahtar sipariş veremez, fiyat değiştiremez, o kuruluşa atamadığınız hiçbir şeyi göremez.',
      guideLink: 'İş ortakları için entegrasyon kılavuzu →',
      guideHint: 'bunu anahtarla birlikte gönderin',
      issueHeading: 'Anahtar oluştur',
      partner: 'İş ortağı',
      chooseOrg: 'Bir kuruluş seçin…',
      label: 'Etiket',
      labelPlaceholder: 'Gece eşitlemesi',
      issuing: 'Oluşturuluyor…',
      issueKey: 'Anahtar oluştur',
      labelHint: 'Etiket sizin içindir — aynı iş ortağının iki anahtarını ayırt etmenizi sağlar.',
      columns: {
        partner: 'İş ortağı',
        label: 'Etiket',
        key: 'Anahtar',
        productsShared: 'Paylaşılan ürün',
        lastUsed: 'Son kullanım',
        status: 'Durum',
      },
      empty: 'Henüz anahtar oluşturulmadı.',
      statusActive: 'Aktif',
      statusRevoked: 'İptal edildi',
      statusExpired: 'Süresi doldu',
      revokeForGood: 'Kalıcı olarak iptal et',
      cancel: 'Vazgeç',
      revokeTitle: 'İş ortağı bir sonraki isteğinde artık okuyamaz',
      revoke: 'İptal et',
      footnote:
        'İptal edilen anahtar bilerek listede kalır — iş ortağının erişimi olduğunu ve artık olmadığını gösteren kayıttır.',
    },
    switch: {
      blockerSwitchOff: 'aşağıdaki anahtar kapalı',
      blockerNoKey: 'hiç anahtar oluşturulmadı',
      blockerNoProducts: 'hiç ürün paylaşılmadı',
      title: 'İş Ortağı API',
      description:
        'Tüm iş ortağı anahtarlarının ana anahtarı. Kapalıyken uçlar hiç yokmuş gibi yanıt verir; hazır olmadan sızan bir anahtar hiçbir şey açmaz.',
      toggleLabel: 'İş ortakları kataloğu okuyabilir',
      on: 'Açık',
      off: 'Kapalı',
      blocked: (reasons) => `Henüz kimse hiçbir şey okuyamaz — ${reasons.join(' ve ')}.`,
      live: 'Yayında. Aktif anahtarı olan iş ortakları kendileriyle paylaşılan ürünleri okuyabilir.',
      readFailed:
        'Anahtarın durumu okunamadı, bu yüzden kapalı gösteriliyor — sunucu da böyle davranır.',
      writeFailed: 'Anahtar değiştirilemedi. Sunucuda en son ne kayıtlıysa o geçerli.',
    },
    catalog: {
      opened: (n) => `${n} açıldı`,
      closed: (n) => `${n} kapatıldı`,
      unchanged: (n) => `${n} değişmedi`,
      skipped: (n) => `${n} atlandı (satışa sunulamaz)`,
      nothingToChange: 'Değişecek bir şey yok',
      applyFailed: 'Değişiklik uygulanamadı',
      title: 'İş ortağı katalog erişimi',
      descriptionBefore:
        'Her iş ortağının neyi okuyabileceği. Siz paylaşmadıkça hiçbir şey paylaşılmaz; bir kategoriyi açmak, içindekileri ',
      descriptionToday: 'bugünkü haliyle',
      descriptionAfter: ' paylaşır — sonradan eklenen ürün siz onu da açana kadar kapalı kalır.',
      partner: 'İş ortağı',
      choosePartner: 'Bir iş ortağı seçin…',
      columns: {
        category: 'Kategori',
        shared: 'Paylaşılan',
        notShared: 'Paylaşılmayan',
      },
      loading: 'Yükleniyor…',
      noProducts: 'Katalogda ürün yok.',
      grownTitle: 'Bu kategori, siz açtıktan sonra büyüdü',
      hideProducts: 'Ürünleri gizle',
      reviewProducts: 'Ürünleri incele',
      loadingProducts: 'Ürünler yükleniyor…',
      openAllUnshared: (n) => `Paylaşılmayan ${n} ürünün hepsini aç`,
      openSelected: (n) => `Seçilenleri aç (${n})`,
      closeAllShared: (n) => `Paylaşılan ${n} ürünün hepsini kapat`,
      sharedBadge: 'paylaşıldı',
      closedBadge: 'kapalı',
    },
  },
}
